import Vue from 'vue'
import Router from 'vue-router'

import adminRouter from '@/components/features/admin/router'
import aircraftDataRouter from '@/components/features/aircraftData/router'
import fleetDataRouter from '@/components/features/fleetData/router'
import flightDataRouter from '@/components/features/flightData/router'
import homeRouter from '@/components/features/home/router'
import issueInvestigationRouter from '@/components/features/issueInvestigation/router'
import maintenanceRouter from '@/components/features/maintenance/router'
import maintenanceDataRouter from '@/components/features/maintenanceData/router'
import pageErrorRouter from '@/components/features/pageErrors/router'
import reportDetailsRouter from '@/components/features/reports/routerDetails'
import reportListRouter from '@/components/features/reports/routerList'
import unauthorizedRouter from '@/components/features/unauthorized/router'
import userRouter from '@/components/features/user/router'
import subscriptionCenterRouter from '@/components/features/subscriptionCenter/router'
import customAlertsRouter from '@/components/features/customAlerts/router'
import DerivedParametersRouter from './components/features/derivedParameters/router'
import powerBiRouter from './components/features/powerBi/router'
import anserToolRouter from './components/features/anserTool/router'
import analyticsRouter from './components/features/analytics/router'
import oktaRouter from './components/features/okta/router'
import oktaAuth from './plugins/oktaAuth'

import store from '@/stores/index'
import {
  USER_CONSTS as UC,
  LOOKUP_CONSTS as LC,
  FEATURE_FLAG_CONSTS as FC,
} from '@/stores/consts'
import { hasRole, hasRoleByName } from '@/utilities/userRoles'
import { hasPermission } from '@/utilities/userPermissions'
import { hasResourcePermission } from '@/utilities/userResourcePermissions'

Vue.use(Router)

const routes = [
  adminRouter,
  aircraftDataRouter,
  homeRouter,
  fleetDataRouter,
  reportListRouter,
  powerBiRouter,
  flightDataRouter,
  maintenanceRouter,
  maintenanceDataRouter,
  ...analyticsRouter,
  reportDetailsRouter,
  issueInvestigationRouter,
  ...anserToolRouter,
  ...pageErrorRouter,
  unauthorizedRouter,
  userRouter,
  subscriptionCenterRouter,
  DerivedParametersRouter,
  customAlertsRouter,
  ...oktaRouter,
  {
    path: '*',
    redirect: `/404`,
  },
]

const router = new Router({
  mode: 'history',
  routes: routes,
  base: process.env.BASE_URL,
})
// global Okta token handling
const oktaNavigationGuard = async (to, from, next) => {
  try {
    if (to.query.code || to.query.state) {
      // handle callback
      await oktaAuth.handleLoginRedirect()
      //const originalUri = oktaAuth.getOriginalUri() || '/'
      const intendedRoute = sessionStorage.getItem('oktaRedirectUri') || '/'
      sessionStorage.removeItem('oktaRedirectUri')
      return next(intendedRoute)
    }
    next()
  } catch (error) {
    console.error('Navigationguard error: ', error)
    next('/login')
  }
}

router.beforeEach(oktaNavigationGuard)

// global route guard
router.beforeEach(async (to, from, next) => {
  // handle Okta callback separately
  if (to.path.includes('/login/callback') || to.query.code || to.query.state) {
    return next()
  }

  // reset the API failure status on each page load
  store.commit('updateApiCallStatus', 'reset')

  if (to.meta && to.meta.breadcrumbs && to.meta.breadcrumbs.length > 0) {
    to.meta.breadcrumbs = to.meta.breadcrumbs.map(breadcrumbObj => {
      const { text } = breadcrumbObj
      breadcrumbObj.text =
        typeof text === 'function' ? text(to) : breadcrumbObj.text
      return breadcrumbObj
    })
  }

  const getUserProfile = async store => {
    if (
      !store.state[UC.NAMESPACE].termsLoaded ||
      !store.state[UC.NAMESPACE].termsAccepted
    ) {
      await Promise.all([
        store.dispatch(`${UC.NAMESPACE}/${UC.GET_USER_PROFILE}`),
        store.dispatch(`${UC.NAMESPACE}/${UC.GET_TANDC_DATA}`),
        store.dispatch(`${LC.NAMESPACE}/${LC.GET_AIRCRAFTS}`),
      ])
      if (
        !store.state[UC.NAMESPACE].termsLoaded ||
        !store.state[UC.NAMESPACE].termsAccepted
      ) {
        return false
      } else {
        const profile = store.getters[`${UC.NAMESPACE}/getProfile`]
        if (window.aptrinsic) {
          window.aptrinsic(
            'identify',
            {
              // User Fields
              id: `ascentia/${profile.userId}`,
              email: profile.userEmail,
            },
            {
              // Account Fields
              id: `ascentia/${profile.company.id}`,
              name: profile.company.name,
            },
          )
        }
      }
    }
    return true
  }

  const redirectHome = (to, next) => {
    const isAirlineUser = hasRoleByName('AirlineUser')
    const isAcmfUser = hasRoleByName('AcmfUser')

    if (isAirlineUser) {
      next({
        name: 'Home',
      })
    }
    if (isAcmfUser && !isAirlineUser) {
      next({
        name: 'Reports',
      })
    }
    if (!isAcmfUser && !isAirlineUser) {
      next({
        name: 'PageNotFound',
      })
    }
  }

  const checkRouteAuth = async (to, next) => {
    let authValid = false

    // Role and Permission authentication
    if (
      to.meta &&
      to.meta.requiresRole &&
      to.meta.requiresPermission &&
      !authValid
    ) {
      authValid = true
      if (
        hasRole(to.meta.requiresRole) &&
        hasPermission(to.meta.requiresPermission)
      ) {
        if (store.state.apiDown) store.commit('updateApiDown', false)
        next()
      } else {
        redirectHome(to, next)
      }
    }

    // Role only authentication
    if (to.meta && to.meta.requiresRole && !authValid) {
      authValid = true
      if (hasRole(to.meta.requiresRole)) {
        if (store.state.apiDown) store.commit('updateApiDown', false)
        return next()
      } else {
        return redirectHome(to, next)
      }
    }

    // Permission only authentication
    if (to.meta && to.meta.requiresPermission && !authValid) {
      authValid = true
      if (hasPermission(to.meta.requiresPermission)) {
        if (store.state.apiDown) store.commit('updateApiDown', false)
        return next()
      } else {
        return redirectHome(to, next)
      }
    }

    // Resource permission level only authentication
    if (to.meta && to.meta.requiresResourcePermission && !authValid) {
      authValid = true
      if (
        hasResourcePermission(
          to.meta.requiresResourcePermission,
          store.state[UC.NAMESPACE].permissions,
        )
      ) {
        if (store.state.apiDown) store.commit('updateApiDown', false)
        return next()
      } else {
        return redirectHome(to, next)
      }
    }

    return authValid
  }

  const isAuth = await oktaAuth.isAuthenticated()

  if (to.name === 'Logout') {
    if (isAuth) {
      await oktaAuth.signOut()
    }

    oktaAuth.tokenManager.remove('accessToken')
    return redirectHome()
  }

  if (to.name === 'Unauthorized') {
    next()
    return
  } else if (isAuth) {
    // make sure to add (|| localhost) in else if to use system user on localhost
    const userProfileCheck = await getUserProfile(store, next)
    if (!userProfileCheck) {
      return
    }
  } else {
    sessionStorage.setItem('oktaRedirectUri', to.fullPath)
    await oktaAuth.signInWithRedirect()
  }

  store.dispatch(`${FC.NAMESPACE}/${FC.GET_ALL_FEATURE_FLAGS}`)
  const validAuth = await checkRouteAuth(to, next)
  if (!validAuth) {
    return next()
  }
})

export default router
